/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import {
    Toolbar,
    TOOLBAR_ITEM_TYPE,
    toolbarItemDecorators,
    useGridApi,
    useModal,
} from '@veeam-vspc/components';

import type { ToolbarItems } from '@veeam-vspc/components';
import type { ResellerCloudBackupResourceMapModel } from '@veeam-vspc/models/web-controllers';

import { useLang } from 'views/providers/LangProvider/hooks';
import { canRemoveResource } from '../../../../utils';
import { QuotaDialog } from '../QuotaDialog';
import addIcon from 'images/actions/add.svg';
import removeIcon from 'images/actions/remove.svg';
import editIcon from 'images/actions/edit.svg';

interface Props {
    items: ResellerCloudBackupResourceMapModel[];
    cloudConnectAgentUids: string[];
}

export const ActionsToolbar = ({ items, cloudConnectAgentUids }: Props) => {
    const lang = useLang();
    const gridApi = useGridApi<ResellerCloudBackupResourceMapModel, void, void>();

    const [quotaDialog, quotaDialogActions] = useModal<ResellerCloudBackupResourceMapModel>({
        render: ({ deactivate, data }) => (
            <QuotaDialog
                data={data}
                existingItems={items}
                cloudConnectAgentUids={cloudConnectAgentUids}
                onConfirm={(newData) => {
                    if (data) {
                        items[items.findIndex(item => item.cloudRepositoryFriendlyName === data.cloudRepositoryFriendlyName)] = newData;
                    } else {
                        items.push(newData);
                    }
                    gridApi.reloadGrid();
                }}
                deactivate={deactivate}
            />
        ),
    });

    const toolbarItems: ToolbarItems = [
        {
            type: TOOLBAR_ITEM_TYPE.button,
            iconSrc: addIcon,
            text: lang.ADD,
            onClick: () => {
                quotaDialogActions.activate();
            },
            decorators: [
            ],
        },
        {
            type: TOOLBAR_ITEM_TYPE.button,
            iconSrc: editIcon,
            text: lang.EDIT,
            onClick: async() => {
                const selected = gridApi.selected[0];
                quotaDialogActions.activate(selected);
            },
            decorators: [
                toolbarItemDecorators.disallowWithoutSingleSelection(),
            ],
        },
        {
            type: TOOLBAR_ITEM_TYPE.button,
            iconSrc: removeIcon,
            text: lang.REMOVE,
            onClick: async() => {
                const selected = gridApi.selected[0];
                if (
                    await canRemoveResource(
                        selected,
                        lang.CANNOT_DELETE_THE_CLOUD_REPOSITORY,
                        lang.THIS_ACTION_WILL_REMOVE_CLOUD_BACKUP,
                        lang
                    )
                ) {
                    items.splice(items.findIndex(item => item.cloudRepositoryFriendlyName === selected.cloudRepositoryFriendlyName), 1);
                    gridApi.reloadGrid();
                }
            },
            decorators: [
                toolbarItemDecorators.disallowWithoutSingleSelection(),
            ],
        },
    ];

    return (
        <>
            <Toolbar items={toolbarItems} />
            {quotaDialog}
        </>
    );
};
